// ─── UserManagementPanel.jsx ──────────────────────────────────────────────────
import React, { useState } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";
import { InlineNotice } from "../../../utils/InlineNotice";
import { useInlineNotice } from "../../../hooks/useInlineNotice";
import { InlineConfirm } from "../../../utils/InlineConfirm";

const BLANK_SCOREKEEPER = { email: "", name: "", password: "" };

export const UserManagementPanel = () => {
  const { adminId, captains, loadData } = useAdmin();
  const { notice, showError, showSuccess, clear } = useInlineNotice();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState(BLANK_SCOREKEEPER);
  const [creating, setCreating] = useState(false);

  const createScorekeeper = async () => {
    clear();
    if (!form.email.trim() || !form.name.trim() || !form.password.trim()) {
      showError("Email, name, and temporary password are all required.");
      return;
    }
    setCreating(true);
    try {
      await supabase.functions.invoke("admin-actions", {
        body: { action: "createScorekeeper", adminId, payload: form },
      });
      showSuccess(
        `Scorekeeper created — ${form.name} (${form.email}). Share the temporary password with them directly.`,
      );
      setForm(BLANK_SCOREKEEPER);
      setShowCreateForm(false);
      loadData();
    } catch (err) {
      showError(`Error: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const removeCaptain = async (captainId, teamId) => {
    try {
      // Remove the team_captain role entry from profile_roles for this team
      const { error } = await supabase
        .from("profile_roles")
        .delete()
        .eq("profile_id", captainId)
        .eq("role", "team_captain")
        .eq("team_id", teamId);

      if (error) throw error;

      // Also invoke the edge function to handle any legacy cleanup
      // (e.g. clearing teams.captain_id, updating profiles.role if needed)
      await supabase.functions.invoke("admin-actions", {
        body: { action: "removeCaptain", adminId, payload: { captainId } },
      });

      showSuccess(
        "Captain removed. You may want to assign a new captain to this team.",
      );
      loadData();
    } catch (err) {
      showError(`Error: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          User Management
        </h2>
        <button
          onClick={() => setShowCreateForm((v) => !v)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {showCreateForm ? "Cancel" : "+ Create Scorekeeper"}
        </button>
      </div>

      <InlineNotice notice={notice} onDismiss={clear} />

      {showCreateForm && (
        <div className="border dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            New Scorekeeper
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              className="border dark:border-gray-500 p-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              className="border dark:border-gray-500 p-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <input
              type="text"
              placeholder="Temporary password"
              value={form.password}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, password: e.target.value }))
              }
              className="border dark:border-gray-500 p-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <button
            onClick={createScorekeeper}
            disabled={creating}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded text-sm font-semibold transition"
          >
            {creating ? "Creating…" : "Create"}
          </button>
        </div>
      )}

      <div>
        <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
          Team Captains
        </h3>
        <div className="space-y-2">
          {captains.map((captain) => (
            <div
              key={captain.id}
              className="border dark:border-gray-600 rounded p-3 flex justify-between items-center"
            >
              <div>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {captain.name}
                </span>
                <span className="text-gray-600 dark:text-gray-400 ml-2">
                  - {captain.email}
                </span>
                {captain.team && (
                  <span className="ml-2 text-sm bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 px-2 py-1 rounded">
                    {captain.team.name}
                  </span>
                )}
              </div>
              <InlineConfirm
                triggerLabel="Remove"
                triggerClassName="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
                message="Remove this team captain? This will NOT delete the team."
                confirmLabel="Remove"
                onConfirm={() => removeCaptain(captain.id, captain.team?.id)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
